// Monitoramento de acesso e trilha de atividades: quem entrou na marca, de onde
// e o que fez depois. Duas features do back (`access-log` e `activity-log`),
// um módulo só no front porque são as duas metades da MESMA tela — a trilha é a
// gaveta que abre na linha da sessão.
//
// Ficou fora de ./calls.js de propósito: apesar de uma das linhas da trilha ser
// "discou para um lead", aqui não há telefonia nenhuma — é auditoria de sessão,
// e serve igual para quem nunca fez uma ligação. O que aproxima os dois
// domínios é a tela, não o dado.

import { api, toApiError } from "./client.js";

// --- Monitoramento de acesso ---
// Espelha a feature `access-log` do back (/v1/access-log). Metadados de sessão;
// o `token` nunca vem do back. Datas chegam como string ISO.

export interface AccessSession {
  sessionId: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string; // última atividade conhecida
  expiresAt: string;
  active: boolean; // sessão ainda válida
  current: boolean; // é a sessão de quem está consultando
}

export interface AccessLogResponse {
  sessions: AccessSession[];
  total: number; // total no recorte atual (ignora limit/offset)
  limit: number;
  offset: number;
  viewerRole: string;
}

export interface AccessLogQuery {
  active?: boolean;
  limit?: number;
  offset?: number;
}

// GET /v1/access-log — uma página de sessões da org (escopo por papel no back:
// seller vê só as próprias). Diretor/gerente veem todas.
export async function getAccessLog(params: AccessLogQuery = {}): Promise<AccessLogResponse> {
  const { data, error } = await api<AccessLogResponse>("/v1/access-log", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/access-log", error);
  return data;
}

// --- Trilha de atividades ---
// Espelha a feature `activity-log` do back (/v1/activity-log). Cada ação de
// escrita autenticada vira uma linha ("o que o usuário fez"). Alimenta a timeline
// que abre ao expandir a linha do usuário no Monitoramento de acesso.

export interface ActivityEntry {
  id: string;
  userId: string;
  sessionId: string | null;
  method: string; // "SESSION" no evento sintético de login
  path: string;
  action: string; // rótulo humano ("Discou para um lead", "Fez login")
  target: string | null;
  statusCode: number;
  createdAt: string; // ISO
}

export interface ActivityLogResponse {
  activities: ActivityEntry[];
}

// GET /v1/activity-log — timeline de UMA sessão (as ações daquela sessão). Escopo
// por papel no back: seller só as próprias.
export async function getSessionActivity(sessionId: string): Promise<ActivityLogResponse> {
  const { data, error } = await api<ActivityLogResponse>("/v1/activity-log", {
    query: { sessionId },
  });
  if (error) throw toApiError("GET /v1/activity-log", error);
  return data;
}
